import { useState, type FormEvent } from "react";
import AutocompleteInput from "./AutocompleteInput";
import type { CompareStatus } from "../types";

interface Props {
  onSubmit: (companies: string[], years: number) => void;
  disabled: boolean;
  statuses?: CompareStatus[];
  onError?: (msg: string) => void;
}

export default function CompareForm({ onSubmit, disabled, statuses, onError }: Props) {
  const [companies, setCompanies] = useState(["", ""]);
  const [years, setYears] = useState(5);

  const handleChange = (index: number, value: string) => {
    setCompanies((prev) => prev.map((c, i) => (i === index ? value : c)));
  };

  const handleAdd = () => {
    if (companies.length < 3) setCompanies((prev) => [...prev, ""]);
  };

  const handleRemove = (index: number) => {
    if (companies.length > 2) setCompanies((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    const valid = companies.map((c) => c.trim()).filter(Boolean);
    if (valid.length < 2) return;
    const unique = new Set(valid.map((c) => c.toLowerCase()));
    if (unique.size < valid.length) {
      if (onError) onError("같은 기업이 중복 입력되어 있습니다.");
      else alert("같은 기업이 중복 입력되어 있습니다.");
      return;
    }
    onSubmit(valid, years);
  };

  const filled = companies.filter((c) => c.trim()).length;
  const isRunning = statuses && statuses.some((s) => s.status === "analyzing");

  const statusIcon = (s?: CompareStatus) => {
    if (!s) return null;
    switch (s.status) {
      case "analyzing":
        return <span className="w-4 h-4 border-2 border-zinc-600 border-t-indigo-400 rounded-full animate-spin shrink-0" />;
      case "done":
        return <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#22c55e" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="shrink-0"><circle cx="12" cy="12" r="10" /><path d="m9 12 2 2 4-4" /></svg>;
      case "error":
        return <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#ef4444" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="shrink-0"><circle cx="12" cy="12" r="10" /><path d="m15 9-6 6" /><path d="m9 9 6 6" /></svg>;
      default:
        return null;
    }
  };

  return (
    <form onSubmit={handleSubmit} className="mb-8">
      <div className="space-y-3">
        {companies.map((company, i) => {
          const s = statuses?.find((st) => st.name === company.trim());
          return (
            <div key={i} className="flex items-center gap-2">
              <div className="flex-1 flex items-center bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 rounded-xl px-4 py-3 gap-2 transition-colors focus-within:border-indigo-500 focus-within:ring-3 focus-within:ring-indigo-500/15">
                <span className="text-zinc-500 text-sm shrink-0 w-5 text-center">{i + 1}</span>
                <AutocompleteInput
                  value={company}
                  onChange={(val) => handleChange(i, val)}
                  placeholder={`기업명 ${i + 1} (예: ${i === 0 ? "삼성전자" : i === 1 ? "SK하이닉스" : "LG전자"})`}
                  disabled={disabled || !!isRunning}
                  className="w-full bg-transparent text-zinc-900 dark:text-white text-sm outline-none font-[inherit] placeholder:text-zinc-400 dark:placeholder:text-zinc-600"
                />
                {statusIcon(s)}
              </div>
              {companies.length > 2 && !isRunning && (
                <button
                  type="button"
                  onClick={() => handleRemove(i)}
                  className="w-9 h-9 flex items-center justify-center rounded-lg text-zinc-500 hover:text-red-400 hover:bg-zinc-100 dark:hover:bg-zinc-800 transition-colors cursor-pointer"
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="m18 6-12 12" /><path d="m6 6 12 12" />
                  </svg>
                </button>
              )}
            </div>
          );
        })}
      </div>

      <div className="flex items-center gap-3 mt-4 max-md:flex-col">
        {companies.length < 3 && !isRunning && (
          <button
            type="button"
            onClick={handleAdd}
            className="flex items-center gap-1.5 px-4 py-2.5 border border-zinc-200 dark:border-zinc-800 rounded-xl text-zinc-500 dark:text-zinc-400 text-sm hover:border-zinc-400 dark:hover:border-zinc-600 hover:text-zinc-700 dark:hover:text-zinc-300 transition-colors cursor-pointer max-md:w-full max-md:justify-center"
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M12 5v14" /><path d="M5 12h14" />
            </svg>
            기업 추가
          </button>
        )}

        <select
          value={years}
          onChange={(e) => setYears(Number(e.target.value))}
          className="px-3 py-2.5 border border-zinc-200 dark:border-zinc-800 rounded-xl bg-zinc-100 dark:bg-zinc-800 text-zinc-700 dark:text-zinc-300 text-sm cursor-pointer font-[inherit] outline-none max-md:w-full"
          disabled={disabled || isRunning}
        >
          <option value={3}>3년</option>
          <option value={5}>5년</option>
          <option value={7}>7년</option>
        </select>

        <button
          type="submit"
          disabled={disabled || isRunning || filled < 2}
          className="flex-1 py-3 px-6 border-none rounded-xl bg-indigo-500 text-white text-sm font-semibold cursor-pointer whitespace-nowrap font-[inherit] transition-colors hover:bg-indigo-600 disabled:bg-zinc-800 disabled:text-zinc-600 disabled:cursor-not-allowed max-md:w-full"
        >
          {isRunning ? "분석 중..." : `비교 시작 (${filled}개)`}
        </button>
      </div>
    </form>
  );
}
